import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';

import { GroupSelectItemInterface, GroupSelectOptionsInterface } from '@components/common';
import Checkbox from '@components/common/Checkbox/Checkbox';

import {
	MAX_WIDTH_OVERFLOW,
	UNKNOWN_CATEGORY_NAME,
	UNKNOWN_SUBCATEGORY_NAME,
	UNKNOWN_SUBCATEGORY_TITLE,
	UNKNOWN_TITLE,
} from '@components/common/GroupSelect/constants';

import CollapsibleBlock from '../../CollapsibleBlock';

interface IVirtualizedRowProps {
	collapsibleBlocksState: Record<string, boolean>;
	onChangeHandler: (isChecked: boolean, ids: Array<string>, categoryName: string) => void;
	toggleCollapsibleBlocks: (name: string) => void;
	option: GroupSelectOptionsInterface;
	style?: Record<string, any>;
	index: number;
	isOverflow?: boolean;
	readOnly?: boolean;
	isUncheckedDisabled?: boolean;
}

const VirtualizedRow = ({
	collapsibleBlocksState,
	onChangeHandler,
	toggleCollapsibleBlocks,
	option,
	style,
	index,
	isOverflow = false,
	readOnly = false,
	isUncheckedDisabled,
}: IVirtualizedRowProps) => {
	const label = option.category?.title || UNKNOWN_TITLE;
	const name = (option.category?.name || UNKNOWN_CATEGORY_NAME) + index.toString();

	const areAllChecked = useCallback((items: GroupSelectItemInterface[]): boolean => {
		return items.every(item => {
			if (item.items) return areAllChecked(item.items);
			return !!item.checked;
		});
	}, []);

	const areSomeChecked = useCallback((items: GroupSelectItemInterface[]): boolean => {
		return items.some(item => {
			if (item.items) return areSomeChecked(item.items);
			return !!item.checked;
		});
	}, []);

	const allChecked = useMemo(() => areAllChecked(option.items || []), [option.items, areAllChecked]);
	const someOfItemsChecked = useMemo(() => areSomeChecked(option.items || []), [option.items, areSomeChecked]);
	const isIndefiniteTopLevel = useMemo(() =>
		someOfItemsChecked && !allChecked, [allChecked, someOfItemsChecked]);
	const isOpen = collapsibleBlocksState[option.category?.name || ''];

	const onChangeCategory = () => {
		const collectIds = (items: Array<GroupSelectItemInterface>): string[] => {
			return items.reduce<string[]>((acc, item) => {
				if (item.id) acc.push(item.id);
				if (item.items) acc.push(...collectIds(item.items));

				return acc;
			}, []);
		};

		const ids = collectIds(option.items);

		onChangeHandler(allChecked, ids, option.category?.name || '');
	};

	return (
		<div
			key={`${option.category?.name || UNKNOWN_CATEGORY_NAME} + ${index}`}
			className={cn({ 'f5gGroupSelectMenuCollapsibleBlockHidden': option.category?.isHidden })}
			style={style}
		>
			<CollapsibleBlock
				isOpen={isOpen}
				label={
					<div className={cn('f5gGroupSelectCheckboxOption')}>
						<Checkbox
							checked={allChecked}
							disabled={readOnly}
							isIndefinite={isIndefiniteTopLevel}
							isOverflow={isOverflow}
							isUncheckedDisabled={isUncheckedDisabled}
							label={label}
							maxWidthOverflow={240}
							name={name}
							onChange={onChangeCategory}
							readOnly={readOnly}
						/>
					</div>
				}
				name={option.category?.name || UNKNOWN_CATEGORY_NAME}
				toggleState={() => toggleCollapsibleBlocks(option.category?.name || UNKNOWN_CATEGORY_NAME)}
			>
				<div className='f5gGroupSelectMenuItemsContainer'>
					{(option.items || []).map((item, index) => {
						if (item.items && item.category) {
							const allCheckedSub = areAllChecked(item.items);
							const someCheckedSub = areSomeChecked(item.items);
							const isIndefiniteSubCategory = someCheckedSub && !allCheckedSub;
							const label = item.category.title || UNKNOWN_SUBCATEGORY_TITLE;
							const name = `${item.category?.name || UNKNOWN_SUBCATEGORY_NAME}-${index}`;
							const subIsOpen = collapsibleBlocksState[item.category.name || ''];
							const onChange = () => onChangeHandler(allCheckedSub, (item.items || [])
								.map(subItem => subItem.id || ''), item.category?.name || '');

							return (
								<div
									key={`${item.category?.name || UNKNOWN_SUBCATEGORY_NAME}-${index}`}
									className='f5gGroupSelectMenuSubcategory'
								>
									<CollapsibleBlock
										isOpen={subIsOpen}
										label={
											<div className={cn('f5gGroupSelectCheckboxOption')}>
												<Checkbox
													checked={allCheckedSub}
													disabled={readOnly}
													isIndefinite={isIndefiniteSubCategory}
													isOverflow={isOverflow}
													label={label}
													maxWidthOverflow={240}
													name={name}
													onChange={onChange}
													readOnly={readOnly}
												/>
											</div>
										}
										name={item.category?.name || UNKNOWN_SUBCATEGORY_NAME}
										toggleState={() =>
											toggleCollapsibleBlocks(item?.category?.name || UNKNOWN_SUBCATEGORY_NAME)}
									>
										<div className='f5gGroupSelectMenuItemsContainer'>
											{(item.items || []).map((subItem, subIndex) => {
												const key = (subItem.id || '') + (item.category?.name || '') + subIndex.toString();
												const label = subItem.isAbsent
													? `${subItem.title || UNKNOWN_TITLE} (absent)`
													: subItem.title || UNKNOWN_TITLE;

												return (
													<div
														key={key}
														className={cn('f5gGroupSelectCheckboxOption', {
															'f5gGroupSelectMenuItemCheckboxHidden': subItem.isHidden,
														})}
													>
														<Checkbox
															checked={!!subItem.checked}
															disabled={readOnly}
															isOverflow={isOverflow}
															isUncheckedDisabled={isUncheckedDisabled}
															label={label}
															maxWidthOverflow={MAX_WIDTH_OVERFLOW}
															name={(subItem.id || '') + (item.category?.name || '') + subIndex.toString()}
															onChange={() =>
																onChangeHandler(!!subItem.checked, [subItem.id || ''], item.category?.name || '')
															}
															readOnly={readOnly}
														/>
													</div>
												);
											})}
										</div>
									</CollapsibleBlock>
								</div>
							);
						}
						const label = item.isAbsent
							? `${item.title || UNKNOWN_TITLE} (absent)`
							: item.title || UNKNOWN_TITLE;
						const onChangeItem = () =>
							onChangeHandler(!!item.checked, [item.id || ''], option.category?.name || '');

						return (
							<div
								key={(item.id || '') + (option.category?.name || '') + index.toString()}
								className={cn('f5gGroupSelectCheckboxOption', {
									'f5gGroupSelectMenuItemCheckboxHidden': item.isHidden,
								})}
							>
								<Checkbox
									checked={!!item.checked}
									disabled={readOnly}
									isOverflow={isOverflow}
									isUncheckedDisabled={isUncheckedDisabled}
									label={label}
									maxWidthOverflow={MAX_WIDTH_OVERFLOW}
									name={(item.id || '') + (option.category?.name || '') + index.toString()}
									onChange={onChangeItem}
									readOnly={readOnly}
								/>
							</div>
						);

					})}
				</div>
			</CollapsibleBlock>
		</div>
	);
};

export default VirtualizedRow;
